import React, { useContext } from 'react';
import Divider from '@mui/material/Divider';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { Avatar, Grid, IconButton, Tooltip, } from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { Feedback, Settings } from '@mui/icons-material';
import { useMutation } from '@apollo/client/react';
import { SelfServiceMenuProps } from './types';
import CurrentUserContext from '@/Contexts/CurrentUser';
import { SignOutDocument, UpdateDefaultCompanyDocument } from '@/graphql/generated/graphql';
import { getInitials } from '@/utils/userInformation';

const SelfServiceMenu: React.FC<SelfServiceMenuProps> = ({ openFeedbackModal, showSettingsModal }) => {

  const { currentUser } = useContext(CurrentUserContext);
  const [switchCompany] = useMutation(UpdateDefaultCompanyDocument, {
    onCompleted: () => {
      // eslint-disable-next-line
      location.href = location.href;
    }
  });

  const [signout] = useMutation(SignOutDocument, {
    onCompleted: (data) => {
      if (data.signOut) {
        location.href = location.href;
      } else {
        console.error(data);
      }
    }
  });

  return (
    <MenuList>
      <Grid
        container
        alignItems='center'
        justifyContent='space-between'
        mb={1}
        size={12}>
        <MenuItem onClick={openFeedbackModal}>
          <Tooltip title="Submit feedback"><Feedback color='primary' /></Tooltip>
        </MenuItem>
        <MenuItem onClick={() => signout()}>
          <Typography variant="body2" color="text.secondary">
            Logout
          </Typography>
        </MenuItem>
      </Grid>
      <Divider />
      <Grid
        container
        alignItems='center'
        justifyContent='space-between'
        mb={1}
        mt={1}
        size={12}>
        <Grid size={4}>
          <Avatar
            src={currentUser?.imageUrl as string}
            sx={{ marginLeft: 2, width: 80, height: 80 }}
          >
            {getInitials(currentUser?.firstName, currentUser?.lastName)}
          </Avatar>
        </Grid>
        <Grid size={8}>
          <Grid container justifyContent="space-between">
            <Grid>
              <Typography variant="body2" fontWeight={600} color="text.secondary">
                {`${currentUser?.firstName} ${currentUser?.lastName}`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {currentUser?.defaultCompany?.name}
              </Typography>
            </Grid>
            <Grid>
              {!currentUser?.isSuperAdmin &&
                <IconButton sx={{ mr: 2 }} onClick={showSettingsModal}>
                  <Settings />
                </IconButton>
              }
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Divider />
      <Grid
        container
        alignItems='center'
        justifyContent='space-between'
        mb={1}
        mt={1}
        size={12}>
        {currentUser?.employers?.map((employer) => {
          if (currentUser.defaultCompany?.id === employer.id) {
            return <React.Fragment key={`${currentUser.id}-${employer.id}-${employer.name}`}></React.Fragment>
          }
          return (
            <MenuItem
              key={`${currentUser.id}-${employer.id}-${employer.name}`}
              onClick={() => switchCompany({ variables: { userId: currentUser.id, companyId: employer.id } })}
              sx={{
                width: '100%',
                paddingY: 2,
              }}
            >
              <Grid container justifyContent='center' size={2}>
                <Avatar
                  src={employer.imageUrl}
                  sx={{ width: 40, height: 40 }}
                >
                  {getInitials(employer.name, undefined)}
                </Avatar>
              </Grid>
              <Grid paddingLeft={1} size={7}>
                <Typography variant="body2" fontWeight={500} color="text.secondary">
                  {employer.name}
                </Typography>
              </Grid>
            </MenuItem>
          );
        })}
        <Tooltip title="Future feature not yet a thing">
          <MenuItem
            key='different-account'
            sx={{
              width: '100%',
              paddingY: 1,
            }}
          >
            <Grid container justifyContent='center' size={2}>
              <Avatar sx={{ width: 35, height: 35 }}> <PersonAddIcon fontSize='small' /> </Avatar>
            </Grid>
            <Grid paddingLeft={1} size={7}>
              <Typography variant="body2" fontWeight={600} color="text.secondary">
                Sign in with a different account
              </Typography>
            </Grid>
          </MenuItem>
        </Tooltip>
      </Grid>
    </MenuList>
  );
}

export default SelfServiceMenu;
